import * as d3 from "d3";
import { dataStreamTypes } from "../../../types";

type GeneData = {
    total_comentions: number;
    new_hits: number;
    odds_ratio: number;
    diffexpr_up: number;
    diffexpr_down: number;
    bbprot_up: number;
    bbprot_down: number;
    methods_count: number;
}

type WorkflowData = {
    gene: string;
    data: GeneData;
}[]

type Graph = {
    nodes: any;
    links: any;
}

export enum Direction {
    up = "up",
    down = "down",
    none = "none"
}

export enum nodeType {
    gene = "gene",
    dataStream = "dataStream",
    output = "output",
    weighted = "weighted",
    sum = "sum",
    score = "score",
    saturation = "saturation"
}

export enum workflowStage {
    geneContribution = "gene-contribution",
    saturation = "saturation",
    weightedScores = "weighted-scores",
    weightedSum = "weighted-sum"
}

type WorkflowNode = {
    type: nodeType;
    base: string;
    direction: Direction;
    value: number;
    subNodes: WorkflowNode[];
    isSub: boolean,
    height: number;
    width: number;
    x: number;
    y: number;
    c?: number,
    label?: string,
    hint?: string
}

type LinkTip = {
    offset: number;
    height: number;
    wasted?: boolean
}

export type WorkflowLink = {
    source: WorkflowNode;
    target: WorkflowNode;
    start: LinkTip;
    end: LinkTip;
}

function sumEvidence(data: WorkflowData, evidence: string): number {
    return data.reduce((sum, g) => sum + g.data[evidence], 0);
}

function maxEvidence(data: WorkflowData, evidence: string): number {
    return Math.max(...data.map(g => g.data[evidence]))
}

function computeTotalValues(data: WorkflowData): GeneData {
    return Object.keys(data[0].data).reduce((acc, key) => ({ ...acc, [key]: sumEvidence(data, key) }), {} as GeneData);
}

function computeGeneValues(data: WorkflowData, totalValues: GeneData): WorkflowData {
    return data.map(d => ({
        gene: d.gene,
        data: Object.keys(d.data).reduce((acc, key) => {
            if (key.startsWith('diffexpr'))
                return { ...acc, [key]: d.data[key] / ((totalValues.diffexpr_up + totalValues.diffexpr_down) || Number.MIN_VALUE) }
            if (key.startsWith('bbprot'))
                return { ...acc, [key]: d.data[key] / ((totalValues.bbprot_up + totalValues.bbprot_down) || Number.MIN_VALUE) }
            return { ...acc, [key]: d.data[key] / (totalValues[key] || Number.MIN_VALUE) }
        },
            {} as GeneData)
    }));
}

function computeGeneNodes(geneValues: WorkflowData, rawValues: WorkflowData): WorkflowNode[] {
    return geneValues.map(g => {
        const rawData = rawValues.find(d => d.gene === g.gene).data
        const total = Object.values(g.data).reduce((sum, v) => sum + v, 0);
        const rawTotal = Object.values(rawData).reduce((sum, v) => sum + (v > 1 ? v : 0), 0);
        const rawNeg = rawData.diffexpr_down + rawData.bbprot_down;
        const rawPos = rawTotal - rawNeg;
        const neg = g.data.diffexpr_down + g.data.bbprot_down;
        const pos = total - neg;
        const direction = total === pos ? Direction.up : total === neg ? Direction.down : Direction.none;
        return {
            type: nodeType.gene,
            label: g.gene,
            hint: rawTotal,
            base: g.gene,
            direction: direction,
            value: total,
            subNodes: direction !== Direction.none ? [] : (() => {
                let nodes = [];
                if (pos)
                    nodes.push({
                        type: nodeType.gene,
                        label: g.gene,
                        hint: rawPos,
                        isSub: true,
                        base: g.gene,
                        direction: Direction.up,
                        value: pos,
                        subNodes: [],
                        height: pos,
                        width: 0,
                        x: 0,
                        y: 0,
                        c: 0
                    });
                if (neg)
                    nodes.push({
                        type: nodeType.gene,
                        hint: rawNeg,
                        isSub: true,
                        base: g.gene,
                        direction: Direction.down,
                        value: neg,
                        subNodes: [],
                        height: neg,
                        width: 0,
                        x: 0,
                        y: 0,
                        c: 0
                    });
                return nodes;
            })(),
            height: total,
            width: 0,
            x: 0,
            y: 0,
            c: 0
        };
    });
}

function computeDataStreamValues(ds: dataStreamTypes, totalValues: GeneData): { total: number, pos: number, neg: number } {
    switch (ds) {
        case dataStreamTypes.miningOverall:
            return {
                total: totalValues.total_comentions,
                pos: totalValues.total_comentions,
                neg: 0
            };

        case dataStreamTypes.miningNovelty:
            return {
                total: totalValues.odds_ratio,
                pos: totalValues.odds_ratio,
                neg: 0
            };
        case dataStreamTypes.diffexpr:
            return {
                total: totalValues.diffexpr_up + totalValues.diffexpr_down,
                pos: totalValues.diffexpr_up,
                neg: totalValues.diffexpr_down
            };
        case dataStreamTypes.otg:
            return {
                total: totalValues.bbprot_up + totalValues.bbprot_down,
                pos: totalValues.bbprot_up,
                neg: totalValues.bbprot_down
            };
        case dataStreamTypes.otp:
            return {
                total: totalValues.methods_count,
                pos: totalValues.methods_count,
                neg: 0
            };

    }
}

function computeDataStreamNodes(dataStreams: dataStreamTypes[], totalValues: GeneData, rawValues: WorkflowData): WorkflowNode[] {
    return dataStreams.map(ds => {
        const { total, pos, neg } = computeDataStreamValues(ds, totalValues);
        const direction = total === pos ? Direction.up : total === neg ? Direction.down : Direction.none;
        return {
            type: nodeType.dataStream,
            // label: getHardcodedLabels(ds),
            base: ds,
            direction: direction,
            value: ds === dataStreamTypes.miningNovelty ? Math.max(...rawValues.map(d => d.data.odds_ratio)) : total,
            hint: ds === dataStreamTypes.miningNovelty ? Math.max(...rawValues.map(d => d.data.odds_ratio)) : total,
            subNodes: direction !== Direction.none ? [] : (() => {
                let nodes = [];
                if (pos)
                    nodes.push({
                        type: nodeType.dataStream,
                        // label: getHardcodedLabels(ds),
                        base: ds,
                        direction: Direction.up,
                        value: pos,
                        hint: pos,
                        isSub: true,
                        subNodes: [],
                        height: pos / total,
                        width: 0,
                        x: 0,
                        y: 0,
                        c: 0
                    });
                if (neg)
                    nodes.push({
                        type: nodeType.dataStream,
                        base: ds,
                        direction: Direction.down,
                        value: neg,
                        hint: neg,
                        isSub: true,
                        subNodes: [],
                        height: neg / total,
                        width: 0,
                        x: 0,
                        y: 0,
                        c: 0
                    });
                return nodes;
            })(),
            height: total > 0,
            width: 0,
            x: 0,
            y: 0,
            c: 0
        };
    });
}

function computeSaturationNodes(dataStramNodes: WorkflowNode[], yScale, layer, w, pw, alpha: { [key: string]: number }): WorkflowNode[] {
    let nodes = []
    dataStramNodes.forEach(n => {
        if (n.height > 0)
            nodes.push({
                type: nodeType.saturation,
                label: `\u03B1 = ${alpha[n.base]}`,
                base: n.base,
                direction: n.direction,
                value: 0,
                subNodes: [],
                height: yScale(0.25),
                width: w,
                x: layer * w + pw.reduce((sum, x, i) => i < layer ? sum + x : sum, 0),
                y: n.y + n.height / 2 - yScale(0.25) / 2,
                c: (n.y + n.height / 2 - yScale(0.25) / 2) / 2,
                input: n.hint,
                alpha: alpha[n.base]
            })
    })
    return nodes
}

function saturation(x: number, alpha: number): number {
    return (2 / (1 + Math.exp(-alpha * x))) - 1;
}

function computeOutputNodes(dataStramNodes: WorkflowNode[], alpha: { [key: string]: number }, weights: { [key: string]: number }): WorkflowNode[] {
    let nodes = [], i = 0;
    dataStramNodes.forEach(n => {
        if (!n.subNodes.length) {
            console.log(n.base, alpha[n.base], n.value)
            const value = saturation(n.value, alpha[n.base])
            nodes.push({
                type: nodeType.output,
                label: `w = ${weights[n.base]}`,
                base: n.base,
                direction: n.direction,
                value: value,
                hint: value,
                subNodes: [],
                height: value,
                width: 0,
                x: 0,
                y: 0,
                c: 0
            });
        }
        else {
            n.subNodes.forEach(sn => {
                const value = saturation(sn.value, alpha[sn.base])
                nodes.push({
                    type: nodeType.output,
                    label: `w = ${weights[n.base]}`,
                    base: sn.base,
                    direction: sn.direction,
                    value: value,
                    hint: value,
                    isSub: true,
                    subNodes: [],
                    height: value,
                    width: 0,
                    x: 0,
                    y: 0,
                    c: 0
                });
            });
        }
    })
    return nodes;
}

function computeWeightedNodes(outputNodes: WorkflowNode[], weights: { [key: string]: number }): WorkflowNode[] {
    return outputNodes.map(n => {
        const value = n.value * weights[n.base];
        return {
            type: nodeType.weighted,
            base: n.base,
            direction: n.direction,
            value: value,
            hint: value,
            subNodes: [],
            height: value,
            width: 0,
            x: 0,
            y: 0,
            c: 0
        }
    });
}

function computeSumNode(weightedNodes: WorkflowNode[]): WorkflowNode[] {
    const up = weightedNodes.reduce((sum, n) => sum + (n.direction === Direction.up ? n.value : 0), 0);
    const down = weightedNodes.reduce((sum, n) => sum + (n.direction === Direction.down ? n.value : 0), 0);
    const total = up + down;
    const direction = total === up ? Direction.up : total === down ? Direction.down : Direction.none;
    let nodes = [];
    if (up)
        nodes.push({
            type: nodeType.sum,
            base: `sum`,
            // label: `Sum`,
            direction: Direction.up,
            value: up,
            hint: up,
            subNodes: [],
            height: up,
            width: 0,
            x: 0,
            y: 0,
            c: 0
        });
    if (down)
        nodes.push({
            type: nodeType.sum,
            base: `sum`,
            direction: Direction.down,
            value: down,
            hint: down,
            subNodes: [],
            height: down,
            width: 0,
            x: 0,
            y: 0,
            c: 0
        });
    return nodes;
    // return {
    //     type: nodeType.sum,
    //     base: `sum`,
    //     label: `Sum`,
    //     direction: direction,
    //     value: total,
    //     hint: total,
    //     subNodes: (() => {
    //         let nodes = [];
    //         if (up)
    //             nodes.push({
    //                 type: nodeType.sum,
    //                 base: `sum`,
    //                 label: `Sum`,
    //                 direction: Direction.up,
    //                 value: up,
    //                 hint: up,
    //                 subNodes: [],
    //                 height: up,
    //                 width: 0,
    //                 x: 0,
    //                 y: 0,
    //                 c: 0
    //             });
    //         if (down)
    //             nodes.push({
    //                 type: nodeType.sum,
    //                 base: `sum`,
    //                 direction: Direction.down,
    //                 value: down,
    //                 hint: down,
    //                 subNodes: [],
    //                 height: down,
    //                 width: 0,
    //                 x: 0,
    //                 y: 0,
    //                 c: 0
    //             });
    //         return nodes;
    //     })(),
    //     height: total,
    //     width: 0,
    //     x: 0,
    //     y: 0,
    //     c: 0
    // }
}

function computeScoreNode(sumNodes: WorkflowNode[]): WorkflowNode {
    const value = sumNodes.reduce((sum, n) => sum + (Number(n.direction === Direction.down) * -2 + 1) * n.value, 0);
    const height = Math.max(...sumNodes.map(n => n.value))
    return {
        type: nodeType.score,
        base: `score`,
        // label: `M_score`,
        direction: value > 0 ? Direction.up : value < 0 ? Direction.down : Direction.none,
        value: Math.abs(value),
        hint: value,
        subNodes: [],
        height: height,
        width: 0,
        x: 0,
        y: 0,
        c: 0
    }
}

function computeMscoreNode(scoreNode: WorkflowNode): WorkflowNode {
    return {
        type: nodeType.score,
        base: `score`,
        label: `M_score`,
        direction: scoreNode.direction,
        value: scoreNode.value,
        hint: scoreNode.value,
        subNodes: [],
        height: Math.abs(scoreNode.value),
        width: 0,
        x: 0,
        y: 0,
        c: 0
    }
}

function computeNodePositions(
    nodes: WorkflowNode[],
    layer: number,
    yScale,
    w: number,
    pw: number[],
    ph: number,
    h: number
) {
    const filledSpace = nodes.reduce((sum, n) => sum + yScale(n.height + ph), 0) - yScale(ph)
    let yOffset = (h - filledSpace) / 2;

    // Compute extra space to distribute evenly
    // const extraSpace = (containerHeight - totalHeight) / Math.max(nodes.length - 1, 1);

    return nodes.flatMap((n, i) => {
        if (!n.subNodes.length) {
            const nn = {
                ...n,
                width: w,
                height: yScale(n.height),
                x: layer * w + pw.reduce((sum, x, i) => i < layer ? sum + x : sum, 0),
                y: yOffset
            };

            yOffset += nn.height + yScale(ph); // Distribute spacing
            return nn;
        }

        // Sub-nodes processing
        let subYOffset = yOffset;
        const ns = n.subNodes.map((sn, i) => {
            const nn = {
                ...sn,
                width: w,
                height: yScale(sn.height),
                x: layer * w + pw.reduce((sum, x, i) => i < layer ? sum + x : sum, 0),
                y: subYOffset
            };
            subYOffset += nn.height;
            return nn;
        });

        yOffset = subYOffset + yScale(ph); // Adjust padding after subNodes
        return ns;
    });
}

function computeGeneStreamLinks(geneNodes: WorkflowNode[], dataStreamNodes: WorkflowNode[], geneValues: WorkflowData, yScale): WorkflowLink[] {
    let links = []
    let streamOffsets = Object.fromEntries(dataStreamNodes.map(n => [`${n.base}_${n.direction}`, 0]))
    geneNodes.forEach(sourceN => {
        const geneData = geneValues.find(d => d.gene === sourceN.base).data
        let geneOffset = 0
        dataStreamNodes.forEach(targetNode => {
            let value = 0
            let filters = { stream: targetNode.base, gene: sourceN.base }
            if (sourceN.direction === Direction.up && targetNode.direction === Direction.up)
                switch (targetNode.base) {
                    case dataStreamTypes.miningOverall:
                        value = geneData.total_comentions
                        break;
                    case dataStreamTypes.miningNovelty:
                        value = geneData.odds_ratio
                        break;
                    case dataStreamTypes.otp:
                        value = geneData.methods_count
                        break;
                    case dataStreamTypes.diffexpr:
                        value = geneData.diffexpr_up
                        filters['inline'] = true
                        break;
                    case dataStreamTypes.otg:
                        value = geneData.bbprot_up
                        filters['inline'] = true
                        break;
                }
            if (sourceN.direction === Direction.down && targetNode.direction === Direction.down) {
                filters['inline'] = false
                switch (targetNode.base) {
                    case dataStreamTypes.diffexpr:
                        value = geneData.diffexpr_down
                        break;
                    case dataStreamTypes.otg:
                        value = geneData.bbprot_down
                        break;
                }
            }
            if (value) {
                value = yScale(value)
                links = [...links, {
                    source: sourceN, target: targetNode,
                    start: { offset: geneOffset, height: value },
                    end: { offset: streamOffsets[`${targetNode.base}_${targetNode.direction}`], height: value },
                    filters: filters
                }]
                geneOffset += value
                streamOffsets[`${targetNode.base}_${targetNode.direction}`] += value
            }
        })

    })
    return links
}

function computeStreamSaturationLinks(streamNodes: WorkflowNode[], saturationNodes: WorkflowNode[], yScale): WorkflowLink[] {
    let links = []
    streamNodes.forEach(sourceN => {
        const targetN = saturationNodes.find(n => n.base === sourceN.base && n.direction === sourceN.direction)
        links = [...links, {
            source: sourceN, target: targetN,
            start: { offset: sourceN.height / 2, height: sourceN.height },
            end: { offset: targetN.height / 2, height: yScale(0.01) },
            type: 'arrow'
        }]
    }
    )
    return links
}

function computeSaturationOutputLinks(saturationNodes: WorkflowNode[], outputNodes: WorkflowNode[], yScale): WorkflowLink[] {
    let links = []
    saturationNodes.forEach(sourceN => {
        const targetN = outputNodes.find(n => n.base === sourceN.base && n.direction === sourceN.direction)
        links = [...links, {
            source: sourceN, target: targetN,
            start: { offset: sourceN.height / 2, height: yScale(0.01) },
            end: { offset: targetN.height / 2, height: targetN.height },
            type: 'arrow'
        }]
    }
    )
    return links
}

function computeOutputWeightLinks(outputNodes: WorkflowNode[], weightedNodes: WorkflowNode[], yScale): WorkflowLink[] {
    let links = []
    outputNodes.forEach(sourceN => {
        const targetN = weightedNodes.find(n => n.base === sourceN.base && n.direction === sourceN.direction)
        links = [...links, {
            source: sourceN, target: targetN,
            start: { offset: 0, height: yScale(sourceN.value) },
            end: { offset: 0, height: yScale(targetN.value) }
        }]
    }
    )
    return links
}

function computeSumLinks(weightedNodes: WorkflowNode[], sumNodes: WorkflowNode[], yScale): WorkflowLink[] {
    let links = []
    let targetOffset = { [Direction.up]: 0, [Direction.down]: 0 }
    weightedNodes.forEach(sourceN => {
        const targetN = sumNodes.find(n => n.direction === sourceN.direction)
        links = [...links, {
            source: sourceN, target: targetN,
            start: { offset: 0, height: yScale(sourceN.value) },
            end: { offset: targetOffset[sourceN.direction], height: yScale(sourceN.value) }
        }]
        targetOffset[sourceN.direction] += yScale(sourceN.value)
    }
    )
    return links
}

function computeScoreLinks(sumNodes: WorkflowNode[], scoreNode: WorkflowNode, yScale): WorkflowLink[] {
    if (sumNodes.length === 1)
        return [{
            source: sumNodes[0], target: scoreNode,
            start: { offset: 0, height: yScale(scoreNode.value) },
            end: { offset: 0, height: yScale(scoreNode.value) },
        }];
    const n = sumNodes.find(n => n.direction === scoreNode.direction)
    const w = sumNodes.find(n => n.direction !== scoreNode.direction)
    const o = scoreNode.direction === Direction.up ? 0 : w.height
    return [
        {
            source: n, target: scoreNode,
            start: { offset: 0, height: yScale(n.value) },
            end: { offset: 0, height: yScale(n.value) },
        },
        {
            source: w, target: scoreNode,
            start: { offset: 0, height: yScale(w.value) },
            end: { offset: n.height - w.height, height: yScale(w.value) },
            // type: 'wasted'
        },
    ]
}

function computeMscoreLinks(scoreNode: WorkflowNode, mscoreNode: WorkflowNode, yScale): WorkflowLink[] {
    return [
        {
            source: scoreNode, target: mscoreNode,
            start: { offset: 0, height: yScale(mscoreNode.value) },
            end: { offset: 0, height: yScale(mscoreNode.value) },
            // type: 'wasted'
        },
    ]
}

export function workflow(data: WorkflowData, alpha, weights, width, height): Graph {
    const nlayer = 8, w = 10, hp = 0.2, avsp = (width - w * nlayer), wp = [avsp * (2 / 8), avsp * (1 / 8), avsp * (1 / 8), avsp * (1 / 8), avsp * (1 / 8), avsp * (1 / 8), avsp * (1 / 8)];
    // width = 1000, height = 500
    const totalValues = computeTotalValues(data);
    const geneValues = computeGeneValues(data, totalValues);
    let geneNodes = computeGeneNodes(geneValues, data).filter(n => n.height > 0);
    let dataStreamNodes = computeDataStreamNodes(Object.values(dataStreamTypes), totalValues, data).filter(n => n.height > 0);
    let outputNodes = computeOutputNodes(dataStreamNodes, alpha, weights).filter(n => n.height > 0);
    let weightedNodes = computeWeightedNodes(outputNodes, weights).filter(n => n.height > 0);
    let sumNodes = computeSumNode(weightedNodes);
    let scoreNode = computeScoreNode(sumNodes);
    let mscoreNode = computeMscoreNode(scoreNode);

    // Compute maxY correctly, including sub-nodes and all padding
    const maxY = geneNodes.reduce((sum, n) => (sum + n.height + hp), 0) - hp;
    const maxY2 = dataStreamNodes.reduce((sum, n) => (sum + n.height + hp), 0) - hp;
    const maxY3 = outputNodes.reduce((sum, n) => (sum + n.height + hp), 0) - hp;
    const maxY4 = weightedNodes.reduce((sum, n) => (sum + n.height + hp), 0) - hp;
    const yScale = d3.scaleLinear()
        .domain([0, Math.max(maxY, maxY2, maxY3, maxY4)])
        .range([0, height]);

    // Compute node positions correctly with distributed space
    geneNodes = computeNodePositions(geneNodes, 0, yScale, w, [], hp, height);
    dataStreamNodes = computeNodePositions(dataStreamNodes, 1, yScale, w, wp, hp, height);
    let saturationNodes = computeSaturationNodes(dataStreamNodes, yScale, 2, w, wp, alpha)
    // saturationNodes = computeNodePositions(saturationNodes, 2, yScale, w, wp, hp + 0.25, height);
    outputNodes = computeNodePositions(outputNodes, 3, yScale, w, wp, hp, height);
    weightedNodes = computeNodePositions(weightedNodes, 4, yScale, w, wp, hp, height);
    sumNodes = computeNodePositions(sumNodes, 5, yScale, w, wp, hp, height);
    const scoreNodes = computeNodePositions([scoreNode], 6, yScale, w, wp, hp, height);
    const mscoreNodes = computeNodePositions([mscoreNode], 7, yScale, w, wp, hp, height);

    const GeneStreamLinks = computeGeneStreamLinks(geneNodes, dataStreamNodes, geneValues, yScale)
    const streamSaturationLinks = computeStreamSaturationLinks(dataStreamNodes, saturationNodes, yScale)
    const saturationOutputLinks = computeSaturationOutputLinks(saturationNodes, outputNodes, yScale)
    const OutputWeightLinks = computeOutputWeightLinks(outputNodes, weightedNodes, yScale)
    const sumLinks = computeSumLinks(weightedNodes, sumNodes, yScale)
    const scoreLinks = computeScoreLinks(sumNodes, scoreNodes[0], yScale)
    const mscoreLinks = computeMscoreLinks(scoreNodes[0], mscoreNodes[0], yScale)

    let graph = {
        nodes: [...geneNodes, ...dataStreamNodes, ...saturationNodes, ...outputNodes, ...weightedNodes, ...sumNodes, ...scoreNodes, ...mscoreNodes].map((n, i) => ({ ...n, id: i })),
        links: [...GeneStreamLinks, ...streamSaturationLinks, ...saturationOutputLinks, ...OutputWeightLinks, ...sumLinks, ...scoreLinks, ...mscoreLinks].map((l, i) => ({ ...l, id: i })),
    };
    
    if (graph.nodes.length) {
        const stages = [
            {
                stage: workflowStage.geneContribution,
                start: graph.nodes.find(n => n.type === nodeType.gene).x,
                end: graph.nodes.find(n => n.type === nodeType.dataStream).x,
            },
            {
                stage: workflowStage.saturation,
                start: graph.nodes.find(n => n.type === nodeType.dataStream).x,
                end: graph.nodes.find(n => n.type === nodeType.output).x,
            },
            {
                stage: workflowStage.weightedScores,
                start: graph.nodes.find(n => n.type === nodeType.output).x,
                end: graph.nodes.find(n => n.type === nodeType.weighted).x,
            },
            {
                stage: workflowStage.weightedSum,
                start: graph.nodes.find(n => n.type === nodeType.weighted).x,
                end: graph.nodes.find(n => n.type === nodeType.score).x,
            }
        ]
        graph = { ...graph, ...{ stages: stages } }
    }
    return graph;
}
